import classNames from 'classnames';
import { JSX, ReactNode } from 'react';
import { FeatureToggles, useFeatureToggle } from '../../../../configuration/featureToggle/toggleHook';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { getSelectedTransportAssignment, transportAssignmentsSlice } from '../../reducers/transportAssignmentsSlice';
import { NoValue } from '../../sharedComponents/NoValue';
import { StatusLabel } from '../../sharedComponents/StatusLabel';
import { Address, AlertConfig } from '../../sharedComponents/common.types';
import { AlertIcon, DateTime } from '../../sharedComponents/table/BaseTableComponents';
import { ColumnDefinition } from '../../sharedComponents/table/SortedTableHeader';
import { DateTimeIntervalCell } from '../TransportAssignmentBaseComponents';
import { getHasActiveIncident } from '../TransportAssignmentUtils';
import { getRowAlertConfiguration } from '../tableRowStyleAndSettings';
import {
    ExecutionEvent,
    ExecutionStatus,
    OriginatedTransportOrder,
    TransportAssignment,
    TransportAssignmentStatus,
    TransportAssignmentType,
    TransportOrderBundle,
    TransportOrderBundleDeliveries,
    Unloading,
} from '../transportAssignment.types';
import { TransportAssignmentTableColumn } from './TransportAssignmentTableColumns';
import { TransportAssignmentsTableActionButton } from './TransportAssignmentsTableActionButton';
import {
    AddressField,
    CellWithCancellationState,
    DateWithTimezone,
    IconCell,
    MultiDateCell,
    NoValueForSelectableUnloadingAddress,
    TransportOrderCapacityOrderVehiclesCell,
} from './TransportAssignmentsTableRowBaseComponents';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import { maxBy } from 'es-toolkit/array';

const filterOutCancelledDeliveries = (transportOrderBundleDeliveries: TransportOrderBundleDeliveries[]) =>
    transportOrderBundleDeliveries.filter(
        (delivery) =>
            delivery.originatedTransportOrders.filter((transportOrder) => !transportOrder.isCancelled).length > 0,
    );

const getTransportOrderBundleDeliveries = (transportOrderBundle: TransportOrderBundle) =>
    transportOrderBundle.status === TransportAssignmentStatus.CANCELLATION
        ? transportOrderBundle.deliveries
        : filterOutCancelledDeliveries(transportOrderBundle.deliveries);

const getVehicleModelNames = (transportOrderBundleDeliveries: TransportOrderBundleDeliveries[]): [string, boolean][] =>
    transportOrderBundleDeliveries.flatMap((delivery) => {
        return delivery.originatedTransportOrders.map<[string, boolean]>((originatedTransportOrder) => [
            originatedTransportOrder.vehicle.model.name.trim(),
            originatedTransportOrder.isCancelled,
        ]);
    });

const TransportOrderBundleVehiclesCell = (props: {
    transportOrderBundleDeliveries: TransportOrderBundleDeliveries[];
}) => {
    const vehicleModelNames = getVehicleModelNames(props.transportOrderBundleDeliveries);
    const notCancelledVehicleModelNames = vehicleModelNames
        .filter(([, isCancelled]) => !isCancelled)
        .map(([modelName]) => modelName);

    if (vehicleModelNames.length === 0) {
        return <NoValue />;
    } else if (vehicleModelNames.length === 1) {
        return (
            <CellWithCancellationState isCancelled={vehicleModelNames[0][1]}>
                <div className="ellipsis-1">{vehicleModelNames[0][0]}</div>
            </CellWithCancellationState>
        );
    } else if (notCancelledVehicleModelNames.length === 1) {
        return <div>{notCancelledVehicleModelNames[0]}</div>;
    } else {
        const listOfVehicles = notCancelledVehicleModelNames.map((modelName, idx) => <p key={idx}>{modelName}</p>);
        const cellContent = (
            <IconCell
                icon="rioglyph-car"
                nr={
                    notCancelledVehicleModelNames.length === 0
                        ? vehicleModelNames.length
                        : notCancelledVehicleModelNames.length
                }
                formattedMessageId="outboundPortal.transportAssignments.table.vehicles"
            />
        );
        return listOfVehicles.length > 0 ? (
            <OverlayTrigger
                placement="top-start"
                overlay={
                    <Tooltip textAlignment="left" width="auto">
                        {listOfVehicles}
                    </Tooltip>
                }
            >
                <div>{cellContent}</div>
            </OverlayTrigger>
        ) : (
            <CellWithCancellationState isCancelled>{cellContent}</CellWithCancellationState>
        );
    }
};

const getVehicleModelGroups = (transportOrderBundleDeliveries: TransportOrderBundleDeliveries[]): [string, boolean][] =>
    transportOrderBundleDeliveries.flatMap((delivery) => {
        return delivery.originatedTransportOrders.map<[string, boolean]>((originatedTransportOrder) => [
            originatedTransportOrder.vehicle.model.modelGroup.trim(),
            originatedTransportOrder.isCancelled,
        ]);
    });

const TransportOrderBundleModelGroupCell = (props: {
    transportOrderBundleDeliveries: TransportOrderBundleDeliveries[];
}) => {
    const { transportOrderBundleDeliveries } = props;

    const modelGroupNamesAndCancelledStatus = getVehicleModelGroups(transportOrderBundleDeliveries);
    const distinctModelGroups = new Set(modelGroupNamesAndCancelledStatus.map(([modelGroup]) => modelGroup));
    const distinctNotCancelledModelGroups = new Set(
        modelGroupNamesAndCancelledStatus.filter(([, isCancelled]) => !isCancelled).map(([modelGroup]) => modelGroup),
    );

    if (modelGroupNamesAndCancelledStatus.length === 0) {
        return <NoValue />;
    } else if (distinctModelGroups.size === 1) {
        return (
            <CellWithCancellationState isCancelled={distinctNotCancelledModelGroups.size === 0}>
                {Array.from(distinctModelGroups)[0]}
            </CellWithCancellationState>
        );
    } else {
        const listOfModelGroups = Array.from(distinctNotCancelledModelGroups).map((line: string, idx) => (
            <p key={idx}>{line}</p>
        ));

        const cellContent = (
            <IconCell
                icon="rioglyph-car"
                nr={
                    distinctNotCancelledModelGroups.size === 0
                        ? distinctModelGroups.size
                        : distinctNotCancelledModelGroups.size
                }
                formattedMessageId="outboundPortal.transportAssignments.table.models"
            />
        );
        return listOfModelGroups.length > 0 ? (
            <OverlayTrigger
                placement="bottom-start"
                overlay={
                    <Tooltip textAlignment="left" width="auto">
                        {listOfModelGroups}
                    </Tooltip>
                }
            >
                <div>{cellContent}</div>
            </OverlayTrigger>
        ) : (
            <CellWithCancellationState isCancelled={distinctNotCancelledModelGroups.size === 0}>
                {cellContent}
            </CellWithCancellationState>
        );
    }
};

const TransportOrderBundleUnloading = (props: { transportOrderBundleDeliveries: TransportOrderBundleDeliveries[] }) => {
    if (props.transportOrderBundleDeliveries.length > 1) {
        const listOfAddresses = props.transportOrderBundleDeliveries.map((delivery, idx) => {
            const address = delivery.unloading.addressAndContact?.address;
            if (address !== undefined) {
                return (
                    <p key={idx}>
                        <span>{address.name}</span>
                        <br />
                        <span>{`${address.postalCode} ${address.city}`}</span>
                    </p>
                );
            }
        });

        return (
            <OverlayTrigger
                placement="top-start"
                overlay={
                    <Tooltip textAlignment="left" width="auto">
                        {listOfAddresses}
                    </Tooltip>
                }
            >
                <div>
                    <IconCell
                        icon="rioglyph-map-marker"
                        nr={props.transportOrderBundleDeliveries.length}
                        formattedMessageId="outboundPortal.transportAssignments.table.addresses"
                    />
                </div>
            </OverlayTrigger>
        );
    } else {
        const address = props.transportOrderBundleDeliveries[0]?.unloading.addressAndContact?.address;
        if (address === undefined) {
            return <NoValue />;
        }
        return <AddressField address={address} />;
    }
};

const UnloadingAddress = (props: { address?: Address; hasSelectableUnloadingAddress: boolean }) => {
    const { address, hasSelectableUnloadingAddress } = props;
    if (address === undefined) {
        return <NoValueForSelectableUnloadingAddress hasSelectableUnloadingAddress={hasSelectableUnloadingAddress} />;
    }
    return <AddressField address={address} />;
};

const TransportBundleUnloadingLocationIds = (props: {
    transportOrderBundleDeliveries: TransportOrderBundleDeliveries[];
}) => {
    const unloadingLocationIds = props.transportOrderBundleDeliveries
        .map((delivery) => delivery.unloading.addressAndContact?.identifier.id)
        .filter((id) => id !== undefined);
    if (unloadingLocationIds.length > 1) {
        return (
            <OverlayTrigger
                placement="top-start"
                overlay={
                    <Tooltip textAlignment="left" width="auto">
                        {unloadingLocationIds.map((id: string, idx) => (
                            <p key={idx}>{id}</p>
                        ))}
                    </Tooltip>
                }
            >
                <div>
                    <IconCell
                        icon="rioglyph-map-marker"
                        nr={unloadingLocationIds.length}
                        formattedMessageId="outboundPortal.common.table.locationIds"
                    />
                </div>
            </OverlayTrigger>
        );
    } else {
        const id = props.transportOrderBundleDeliveries[0]?.unloading.addressAndContact?.identifier.id;
        return id === undefined ? <NoValue /> : id;
    }
};

// eslint-disable-next-line max-lines-per-function
const renderTableDataContent = (
    column: TransportAssignmentTableColumn,
    transportAssignment: TransportAssignment,
    rowConfig: AlertConfig[],
): JSX.Element | ReactNode => {
    const isSingleOrder = transportAssignment.type === TransportAssignmentType.TRANSPORT_ORDER;
    const isBundle = transportAssignment.type === TransportAssignmentType.TRANSPORT_ORDER_BUNDLE;
    const isCapacityOrder = transportAssignment.type === TransportAssignmentType.TRANSPORT_CAPACITY_ORDER;
    const isCancelled = transportAssignment.status === TransportAssignmentStatus.CANCELLATION;
    switch (column) {
        case TransportAssignmentTableColumn.HAS_CHANGES:
            return <AlertIcon rowConfig={rowConfig} />;
        case TransportAssignmentTableColumn.MODEL:
            if (isSingleOrder) {
                return (
                    <CellWithCancellationState isCancelled={isCancelled}>
                        <div className="ellipsis-1">{transportAssignment.vehicle.model.name}</div>
                    </CellWithCancellationState>
                );
            }
            if (isBundle) {
                return (
                    <TransportOrderBundleVehiclesCell
                        transportOrderBundleDeliveries={getTransportOrderBundleDeliveries(transportAssignment)}
                    />
                );
            }
            if (isCapacityOrder) {
                return <TransportOrderCapacityOrderVehiclesCell transportCapacityOrder={transportAssignment} />;
            }
            return <NoValue />;
        case TransportAssignmentTableColumn.MODEL_GROUP:
            if (isSingleOrder) {
                return (
                    <CellWithCancellationState isCancelled={isCancelled}>
                        {transportAssignment.vehicle.model.modelGroup}
                    </CellWithCancellationState>
                );
            }
            if (isBundle) {
                return (
                    <TransportOrderBundleModelGroupCell
                        transportOrderBundleDeliveries={getTransportOrderBundleDeliveries(transportAssignment)}
                    />
                );
            }
            return <NoValue />;
        case TransportAssignmentTableColumn.ORDER_ISSUE_DATE:
            return (
                <DateTime
                    dateTime={transportAssignment.orderIssuedAt}
                    timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
                />
            );
        case TransportAssignmentTableColumn.FROM:
            if (transportAssignment.loading.addressAndContact.address === undefined) {
                return <NoValue />;
            }
            return <AddressField address={transportAssignment.loading.addressAndContact.address} />;
        case TransportAssignmentTableColumn.FROM_LOCATION_ID:
            return transportAssignment.loading.addressAndContact.identifier.id;
        case TransportAssignmentTableColumn.REQUESTED_PICKUP:
            return (
                <DateTimeIntervalCell
                    dateTimeInterval={transportAssignment.loading.requestedPickUpDate}
                    timeZone={transportAssignment.loading.timeZone}
                />
            );
        case TransportAssignmentTableColumn.TO:
            if (isBundle) {
                return (
                    <TransportOrderBundleUnloading
                        transportOrderBundleDeliveries={getTransportOrderBundleDeliveries(transportAssignment)}
                    />
                );
            } else {
                return (
                    <UnloadingAddress
                        address={transportAssignment.unloading.addressAndContact?.address}
                        hasSelectableUnloadingAddress={
                            isSingleOrder ? transportAssignment.hasSelectableUnloadingAddress : false
                        }
                    />
                );
            }
        case TransportAssignmentTableColumn.TO_LOCATION_ID:
            if (isBundle) {
                return (
                    <TransportBundleUnloadingLocationIds
                        transportOrderBundleDeliveries={getTransportOrderBundleDeliveries(transportAssignment)}
                    />
                );
            } else {
                if (transportAssignment.unloading.addressAndContact?.identifier.id === undefined) {
                    return (
                        <NoValueForSelectableUnloadingAddress
                            hasSelectableUnloadingAddress={
                                isSingleOrder ? transportAssignment.hasSelectableUnloadingAddress : false
                            }
                        />
                    );
                } else {
                    return transportAssignment.unloading.addressAndContact.identifier.id;
                }
            }
        case TransportAssignmentTableColumn.REQUESTED_DELIVERY: {
            const requestedDeliveryDates = isBundle
                ? getTransportOrderBundleDeliveries(transportAssignment).map((delivery) => ({
                      date: delivery.unloading.requestedDeliveryDate,
                      timeZone: delivery.unloading.timeZone,
                  }))
                : [
                      {
                          date: transportAssignment.unloading.requestedDeliveryDate,
                          timeZone: transportAssignment.unloading.timeZone,
                      },
                  ];
            return <MultiDateCell dates={requestedDeliveryDates} isCancelled={isCancelled} />;
        }
        case TransportAssignmentTableColumn.MEANS_OF_TRANSPORT:
            return (
                transportAssignment.meansOfTransport?.id ?? (
                    <NoValue
                        messageId={
                            isCancelled
                                ? undefined
                                : 'outboundOrderBook.transportAssignments.table.enterMeansOfTransportId'
                        }
                    />
                )
            );
        case TransportAssignmentTableColumn.DELIVERY: {
            const dates: DateWithTimezone[] = getDeliveryDates(transportAssignment);
            return <MultiDateCell dates={dates} isCancelled={isCancelled} />;
        }
        case TransportAssignmentTableColumn.PICKUP: {
            const dates: DateWithTimezone[] = getPickupDates(transportAssignment).map((date) => {
                return { date, timeZone: transportAssignment.loading.timeZone };
            });
            return <MultiDateCell dates={dates} isCancelled={isCancelled} />;
        }
    }
};

const getPickupDates: (assignment: TransportAssignment) => (Date | undefined)[] = (assignment: TransportAssignment) => {
    const confirmedPickupDate = assignment.loading.confirmedPickUpDate;

    const getNewestLoading = (executionEvents: ExecutionEvent[]) => {
        const loadedEvents = executionEvents.filter((event) => event.eventStatus === ExecutionStatus.LOADED);
        const reference = maxBy(loadedEvents, (event) => event.eventAt.getTime());

        return reference !== undefined ? reference.eventAt : confirmedPickupDate;
    };

    const filterOutCancelled = (to: OriginatedTransportOrder) => {
        if (assignment.status === TransportAssignmentStatus.CANCELLATION) {
            return true;
        } else {
            return !to.isCancelled;
        }
    };

    switch (assignment.type) {
        case TransportAssignmentType.TRANSPORT_CAPACITY_ORDER:
            return [confirmedPickupDate];
        case TransportAssignmentType.TRANSPORT_ORDER:
            return [getNewestLoading(assignment.executionEvents)];
        case TransportAssignmentType.TRANSPORT_ORDER_BUNDLE:
            return assignment.deliveries
                .flatMap((delivery) => delivery.originatedTransportOrders)
                .filter(filterOutCancelled)
                .map((to) => getNewestLoading(to.executionEvents));
    }
};
const getDeliveryDates: (assignment: TransportAssignment) => DateWithTimezone[] = (assignment: TransportAssignment) => {
    const getNewestUnloading = (executionEvents: ExecutionEvent[], unloading: Unloading) => {
        const unloadedEvents = executionEvents.filter((event) => event.eventStatus === ExecutionStatus.UNLOADED);
        const reference = maxBy(unloadedEvents, (event) => event.eventAt.getTime());

        const date = reference !== undefined ? reference.eventAt : unloading.confirmedDeliveryDate;
        return { date, timeZone: unloading.timeZone };
    };

    const filterOutCancelled = (to: OriginatedTransportOrder) => {
        if (assignment.status === TransportAssignmentStatus.CANCELLATION) {
            return true;
        } else {
            return !to.isCancelled;
        }
    };

    switch (assignment.type) {
        case TransportAssignmentType.TRANSPORT_CAPACITY_ORDER:
            return [
                {
                    date: assignment.unloading.confirmedDeliveryDate,
                    timeZone: assignment.unloading.timeZone,
                },
            ];
        case TransportAssignmentType.TRANSPORT_ORDER:
            return [getNewestUnloading(assignment.executionEvents, assignment.unloading)];
        case TransportAssignmentType.TRANSPORT_ORDER_BUNDLE: {
            return assignment.deliveries.flatMap((delivery) =>
                delivery.originatedTransportOrders.filter(filterOutCancelled).map((to) => {
                    return getNewestUnloading(to.executionEvents, delivery.unloading);
                }),
            );
        }
    }
};

const TableRowData = (props: {
    column: TransportAssignmentTableColumn;
    transportAssignment: TransportAssignment;
    rowConfig: AlertConfig[];
    isArchiveTab: boolean;
}) => {
    const { column, transportAssignment, rowConfig, isArchiveTab } = props;
    const isSingleOrder = transportAssignment.type === TransportAssignmentType.TRANSPORT_ORDER;
    const isCapacityOrder = transportAssignment.type === TransportAssignmentType.TRANSPORT_CAPACITY_ORDER;
    const trackingFeatureEnabled = useFeatureToggle(FeatureToggles.TRACKING, false).value;

    const assignmentHasTrackingEnabled = trackingFeatureEnabled && transportAssignment.isAutomaticallyTracked;

    switch (column) {
        case TransportAssignmentTableColumn.EXTERNAL_ID:
            return (
                <td className="ellipsis-1" key={column}>
                    {isCapacityOrder
                        ? transportAssignment.bundleId
                        : isSingleOrder
                          ? transportAssignment.externalOrderId
                          : transportAssignment.bundleId}
                </td>
            );
        case TransportAssignmentTableColumn.STATUS:
            return (
                <>
                    <td key={column} className="no-line-wrap">
                        {assignmentHasTrackingEnabled && (
                            <>
                                <span
                                    className={classNames(
                                        'rioglyph',
                                        'text-size-18',
                                        'padding-right-5',
                                        'rioglyph rioglyph-geofence',
                                        'text-color-highlight-light',
                                        'text-size-20',
                                    )}
                                />
                            </>
                        )}
                        <span
                            className={assignmentHasTrackingEnabled || !trackingFeatureEnabled ? '' : 'margin-left-25'}
                        >
                            <StatusLabel status={transportAssignment.status} />
                        </span>
                    </td>
                </>
            );
        case TransportAssignmentTableColumn.ACTION:
            return isArchiveTab ? null : (
                <td className="text-center" key={column}>
                    <TransportAssignmentsTableActionButton transportAssignment={transportAssignment} />
                </td>
            );
        default:
            return <td key={column}>{renderTableDataContent(column, transportAssignment, rowConfig)}</td>;
    }
};

export const TransportAssignmentRow = (props: {
    transportAssignment: TransportAssignment;
    isArchivedTab: boolean;
    columnDefinition: ColumnDefinition<TransportAssignmentTableColumn>[];
    selectionCell?: ReactNode;
}) => {
    const { id, status, hasUnacknowledgedExternalChanges, type } = props.transportAssignment;
    const selectedAssignment = useAppSelector(getSelectedTransportAssignment);
    const dispatch = useAppDispatch();

    const isSelected = selectedAssignment?.id === id;
    const hasActiveIncident = getHasActiveIncident(props.transportAssignment);
    const isCapacity = type === TransportAssignmentType.TRANSPORT_CAPACITY_ORDER;
    const rowConfig = getRowAlertConfiguration(hasUnacknowledgedExternalChanges, status, hasActiveIncident, isCapacity);
    const highlight = rowConfig.map((config) => config.highlight)[0];

    const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
        /* Ignore click events from inside a checkbox cell */
        const clickedElement = event.target as HTMLElement;
        const isCheckboxCell = clickedElement.closest('.table-checkbox');
        if (isCheckboxCell) {
            return;
        }

        if (isSelected) {
            dispatch(transportAssignmentsSlice.actions.deselectTransportAssignment());
        } else {
            dispatch(transportAssignmentsSlice.actions.selectTransportAssignment(props.transportAssignment));
        }
    };

    return (
        <tr
            key={id}
            onClick={handleRowClick}
            className={classNames('cursor-pointer', highlight, { active: isSelected })}
        >
            {props.selectionCell}
            {props.columnDefinition.map((def) => (
                <TableRowData
                    key={def.column}
                    column={def.column}
                    transportAssignment={props.transportAssignment}
                    rowConfig={rowConfig}
                    isArchiveTab={props.isArchivedTab}
                />
            ))}
        </tr>
    );
};
